import { useCallback, useState } from 'react';
import Cropper, { type Area } from 'react-easy-crop';
import { Modal } from '../../components/modal';
import { Symbol } from '../../components/symbol';

// Crop a banner to the exact shape its placement is drawn at in the customer
// app, before it is uploaded. The app fills each slot edge to edge (cover), so
// an image of any other shape loses whatever falls outside — this lets the
// admin choose what stays instead of the phone choosing for them.
//
// The result is re-encoded as a JPEG at the placement's target width, which also
// keeps a 2–5 MB PNG export from a design tool down to a few hundred KB for
// customers on mobile data.

export interface CropSpec {
  /** Width / height of the slot in the app. */
  ratio: number;
  /** Pixel width of the exported image; height follows from [ratio]. */
  outputWidth: number;
  /**
   * Fractions of the height the app draws over (header + pills at the top,
   * search bar at the bottom). Shaded in the cropper so text stays clear.
   */
  coveredTop?: number;
  coveredBottom?: number;
}

interface Props {
  /** Object URL or same-origin URL of the source image. */
  src: string;
  spec: CropSpec;
  onCancel: () => void;
  onDone: (file: File) => void;
  busy?: boolean;
}

export function BannerCropper({ src, spec, onCancel, onDone, busy = false }: Props) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [area, setArea] = useState<Area | null>(null);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onCropComplete = useCallback((_: Area, pixels: Area) => setArea(pixels), []);

  const outputHeight = Math.round(spec.outputWidth / spec.ratio);
  // Fewer real pixels than the export → the phone shows an upscaled, soft image.
  const lowRes = !!area && area.width < spec.outputWidth * 0.6;

  const top = (spec.coveredTop ?? 0) * 100;
  const bottom = 100 - (spec.coveredBottom ?? 0) * 100;
  const shade = 'rgba(20, 8, 14, 0.42)';
  const cropAreaStyle =
    spec.coveredTop || spec.coveredBottom
      ? {
          background: `linear-gradient(to bottom, ${shade} 0%, ${shade} ${top}%, transparent ${top}%, transparent ${bottom}%, ${shade} ${bottom}%, ${shade} 100%)`,
        }
      : undefined;

  const apply = async () => {
    if (!area) return;
    setWorking(true);
    setError(null);
    try {
      onDone(await cropToFile(src, area, spec.outputWidth, outputHeight));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not crop this image');
    } finally {
      setWorking(false);
    }
  };

  const disabled = busy || working || !area;

  return (
    <Modal
      title="Crop banner"
      description={`Drag to position and zoom to fit. Saved at ${spec.outputWidth} × ${outputHeight}px — exactly the shape the app shows.`}
      size="lg"
      busy={busy || working}
      onClose={onCancel}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={busy || working}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={() => void apply()} disabled={disabled}>
            {busy || working ? 'Uploading…' : 'Crop and upload'}
          </button>
        </>
      }
    >
      <div className="relative h-[min(60vh,520px)] w-full overflow-hidden rounded-[12px] bg-ink-900">
        <Cropper
          image={src}
          crop={crop}
          zoom={zoom}
          minZoom={1}
          maxZoom={4}
          aspect={spec.ratio}
          onCropChange={setCrop}
          onZoomChange={setZoom}
          onCropComplete={onCropComplete}
          objectFit="contain"
          style={cropAreaStyle ? { cropAreaStyle } : undefined}
        />
      </div>

      <div className="mt-4 flex items-center gap-3">
        <Symbol name="zoom_out" size={18} className="text-ink-500" />
        <input
          type="range"
          min={1}
          max={4}
          step={0.01}
          value={zoom}
          onChange={(e) => setZoom(Number(e.target.value))}
          aria-label="Zoom"
          className="flex-1 accent-admin"
        />
        <Symbol name="zoom_in" size={18} className="text-ink-500" />
      </div>

      {cropAreaStyle && (
        <p className="mt-3 text-[13px] text-ink-500">
          The shaded bands sit under the app’s header, category pills and search bar. Keep offer text and faces in the clear middle.
        </p>
      )}
      {lowRes && (
        <p className="mt-2 text-[13px] text-warning-ink">
          This area is only {Math.round(area!.width)}px wide, so it will look soft on phones. Zoom out or use a larger image.
        </p>
      )}
      {error && <p className="mt-2 text-[13px] text-danger-ink">{error}</p>}
    </Modal>
  );
}

/** Draw [area] of [src] onto a canvas of the target size and encode it as JPEG. */
async function cropToFile(src: string, area: Area, width: number, height: number): Promise<File> {
  const img = await loadImage(src);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Your browser could not prepare the image');
  // JPEG has no transparency: transparent PNG areas would otherwise turn black.
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, area.x, area.y, area.width, area.height, 0, 0, width, height);

  let blob: Blob | null;
  try {
    blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.86));
  } catch {
    // A cross-origin image without CORS headers "taints" the canvas.
    throw new Error('This image is hosted elsewhere and cannot be cropped here. Download it and upload the file instead.');
  }
  if (!blob) throw new Error('Could not encode the cropped image');
  return new File([blob], 'banner.jpg', { type: 'image/jpeg' });
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Could not load the image to crop'));
    img.src = src;
  });
}
